/**
 * ─── Biometric Integration Service ──────────────────────────
 * Handles biometric device communication, attendance log parsing,
 * and mobile GPS/photo-based attendance.
 *
 * Supported integration patterns:
 *  1. TCP Socket — direct device communication (ZKTeco, Suprema, Hikvision)
 *  2. API Pull — cloud-based biometric APIs
 *  3. CSV Import — manual export from device software
 *  4. Mobile App — GPS + selfie photo capture
 *
 * Metadata-driven: device config per tenant, attendance rules per branch.
 */

import type {
  BiometricDevice, BiometricDeviceType, BiometricProtocol,
  AttendanceRecord, AttendanceSource, AttendanceType,
} from './hris.schema'

// ─── Biometric Log Entry (raw from device) ───────────────────

export interface RawBiometricLog {
  deviceId: string
  deviceName: string
  terminalId?: string
  employeeId?: string       // From device user mapping
  enrollId?: string          // Biometric enrollment number
  timestamp: number          // Unix ms
  type: 'check_in' | 'check_out' | 'break_out' | 'break_in' | 'overtime_in' | 'overtime_out'
  verificationMethod: 'fingerprint' | 'face' | 'card' | 'pin' | 'password'
  rawData?: string           // Original log entry string
}

// ─── Device Communication Interface ──────────────────────────

export interface BiometricDeviceDriver {
  /** Connect to device */
  connect(config: BiometricDevice): Promise<boolean>
  /** Disconnect from device */
  disconnect(): Promise<void>
  /** Test connection */
  ping(): Promise<boolean>
  /** Pull attendance logs since timestamp */
  pullLogs(since: number): Promise<RawBiometricLog[]>
  /** Get device time */
  getDeviceTime(): Promise<Date>
  /** Set device time */
  setDeviceTime(time: Date): Promise<void>
  /** Get device info */
  getDeviceInfo(): Promise<Record<string, unknown>>
}

// ═══════════════════════════════════════════════════════════════
// Device Driver Factory
// ═══════════════════════════════════════════════════════════════

class ZKTecoDriver implements BiometricDeviceDriver {
  private config: BiometricDevice | null = null

  async connect(config: BiometricDevice): Promise<boolean> {
    this.config = config
    // In production: open TCP socket to config.ipAddress:config.port
    // using ZKTeco SDK protocol (UDP command packets)
    console.log(`[Biometric] Connected to ZKTeco device at ${config.ipAddress}:${config.port}`)
    return true
  }

  async disconnect(): Promise<void> {
    console.log('[Biometric] Disconnected from ZKTeco device')
    this.config = null
  }

  async ping(): Promise<boolean> {
    if (!this.config?.ipAddress) return false
    // In production: send CMD_CONNECT packet, expect CMD_ACK
    return true
  }

  async pullLogs(since: number): Promise<RawBiometricLog[]> {
    // In production: send CMD_ATTLOG_RRQ, parse response
    // Each record: enrollId, timestamp, verification method
    console.log(`[Biometric] Pulling attendance logs since ${new Date(since).toISOString()}`)
    return []
  }

  async getDeviceTime(): Promise<Date> {
    return new Date()
  }

  async setDeviceTime(time: Date): Promise<void> {
    console.log(`[Biometric] Setting device time to ${time.toISOString()}`)
  }

  async getDeviceInfo(): Promise<Record<string, unknown>> {
    return {
      manufacturer: 'ZKTeco',
      model: 'FaceDepot-7A',
      firmware: '4.3.2',
      serialNumber: this.config?.serialNumber,
    }
  }
}

// ─── Driver Registry ─────────────────────────────────────────

const driverRegistry = new Map<BiometricDeviceType, () => BiometricDeviceDriver>()

driverRegistry.set('zkteco', () => new ZKTecoDriver())
// driverRegistry.set('suprema', () => new SupremaDriver())
// driverRegistry.set('hikvision', () => new HikvisionDriver())

// ═══════════════════════════════════════════════════════════════
// Biometric Service
// ═══════════════════════════════════════════════════════════════

export class BiometricService {
  private activeDrivers = new Map<string, BiometricDeviceDriver>()

  /**
   * Get or create a driver for a device.
   */
  async getDriver(device: BiometricDevice): Promise<BiometricDeviceDriver> {
    const existing = this.activeDrivers.get(device.id)
    if (existing) return existing

    const factory = driverRegistry.get(device.deviceType)
    if (!factory) throw new Error(`No driver for device type: ${device.deviceType}`)

    const driver = factory()
    await driver.connect(device)
    this.activeDrivers.set(device.id, driver)
    return driver
  }

  /**
   * Pull attendance logs from a biometric device and normalize them.
   */
  async syncDeviceLogs(device: BiometricDevice, since?: number): Promise<RawBiometricLog[]> {
    const driver = await this.getDriver(device)
    const sinceTime = since ?? (device.lastSyncAt ?? Date.now() - 24 * 60 * 60 * 1000)
    const rawLogs = await driver.pullLogs(sinceTime)

    // Tag logs with device info
    return rawLogs.map(log => ({
      ...log,
      deviceId: device.id,
      deviceName: device.name,
      terminalId: device.terminalId ?? log.terminalId,
    }))
  }

  /**
   * Convert raw biometric logs to standardized AttendanceRecords.
   * Maps device enrollId → employeeId using the employee mapping.
   */
  static normalizeLogs(
    rawLogs: RawBiometricLog[],
    tenantId: string,
    enrollToEmployeeMap: Map<string, string>,  // enrollId → employeeId
    employeeNameMap: Map<string, string>,       // employeeId → fullName
    shiftMap?: Map<string, { startTime: string; endTime: string; graceMinutes: number }>,
  ): Omit<AttendanceRecord, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'version' | 'createdBy' | 'updatedBy'>[] {
    return rawLogs.map(log => {
      const employeeId = enrollToEmployeeMap.get(log.enrollId ?? '') ?? 'unknown'
      const employeeName = employeeNameMap.get(employeeId) ?? 'Unknown Employee'
      const attendanceDate = new Date(log.timestamp).toISOString().slice(0, 10)
      const shift = shiftMap?.get(employeeId)

      // Map biometric types to attendance types
      const typeMap: Record<string, AttendanceType> = {
        'check_in': 'time_in',
        'check_out': 'time_out',
        'break_out': 'break_out',
        'break_in': 'break_in',
        'overtime_in': 'overtime_in',
        'overtime_out': 'overtime_out',
      }

      // Compute late/undertime based on shift
      let isLate = false
      let lateMinutes = 0
      if (log.type === 'check_in' && shift) {
        const [scheduledH, scheduledM] = shift.startTime.split(':').map(Number)
        const logDate = new Date(log.timestamp)
        const scheduledTime = (scheduledH! * 60 + scheduledM!) + (shift.graceMinutes ?? 0)
        const actualTime = logDate.getHours() * 60 + logDate.getMinutes()
        if (actualTime > scheduledTime) {
          isLate = true
          lateMinutes = actualTime - scheduledTime
        }
      }

      return {
        tenantId,
        employeeId,
        employeeName,
        attendanceDate,
        logTime: log.timestamp,
        type: typeMap[log.type] ?? 'time_in',
        source: 'biometric' as AttendanceSource,
        deviceId: log.deviceId,
        deviceName: log.deviceName,
        terminalId: log.terminalId,
        isLate,
        lateMinutes,
        isOvertime: log.type.startsWith('overtime'),
        overtimeMinutes: 0,
        isUndertime: false,
        undertimeMinutes: 0,
        shiftId: undefined,
        status: 'approved',
        notes: `Biometric: ${log.verificationMethod}`,
      }
    })
  }

  /**
   * Parse a CSV export from biometric software (BioTime, ZKAccess, etc.).
   * Supports common formats with column headers.
   */
  static parseCSV(csvContent: string, deviceId: string, deviceName: string): RawBiometricLog[] {
    const lines = csvContent.trim().split('\n')
    if (lines.length < 2) return []

    // Auto-detect CSV format based on headers
    const headers = lines[0]!.toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''))

    // Common column name mappings
    const colMap = {
      enrollId: headers.findIndex(h => h.includes('enroll') || h.includes('user') || h.includes('emp_id') || h.includes('id')),
      timestamp: headers.findIndex(h => h.includes('time') || h.includes('date')),
      type: headers.findIndex(h => h.includes('type') || h.includes('status') || h.includes('in_out') || h.includes('mode')),
      verification: headers.findIndex(h => h.includes('verify') || h.includes('method') || h.includes('mode')),
      terminalId: headers.findIndex(h => h.includes('terminal') || h.includes('device') || h.includes('machine')),
    }

    const logs: RawBiometricLog[] = []
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i]!.split(',').map(c => c.trim().replace(/"/g, ''))
      if (cols.length < 2) continue

      const enrollId = colMap.enrollId >= 0 ? cols[colMap.enrollId] : undefined
      const timeStr = colMap.timestamp >= 0 ? cols[colMap.timestamp] : undefined
      const typeStr = colMap.type >= 0 ? cols[colMap.type]?.toLowerCase() : undefined
      const verifyStr = colMap.verification >= 0 ? cols[colMap.verification] : undefined
      const terminalId = colMap.terminalId >= 0 ? cols[colMap.terminalId] : undefined

      if (!enrollId || !timeStr) continue

      const timestamp = new Date(timeStr).getTime()
      if (isNaN(timestamp)) continue

      // Determine type
      let logType: RawBiometricLog['type'] = 'check_in'
      if (typeStr?.includes('out') || typeStr?.includes('checkout')) logType = 'check_out'
      else if (typeStr?.includes('ot') || typeStr?.includes('overtime')) {
        logType = logType === 'check_in' ? 'overtime_in' : 'overtime_out'
      }

      // Determine verification method
      let verification: RawBiometricLog['verificationMethod'] = 'fingerprint'
      if (verifyStr?.includes('face')) verification = 'face'
      else if (verifyStr?.includes('card') || verifyStr?.includes('rfid')) verification = 'card'
      else if (verifyStr?.includes('pin') || verifyStr?.includes('password')) verification = 'pin'

      logs.push({
        deviceId,
        deviceName,
        terminalId,
        enrollId,
        timestamp,
        type: logType,
        verificationMethod: verification,
        rawData: cols.join(','),
      })
    }

    return logs
  }

  /**
   * Generate a mobile attendance record (GPS + photo capture).
   * Used by the mobile app for field employees.
   */
  static createMobileAttendance(
    tenantId: string,
    employeeId: string,
    employeeName: string,
    type: AttendanceType,
    latitude: number,
    longitude: number,
    locationName?: string,
    photoUrl?: string,
    gpsAccuracy?: number,
  ): Omit<AttendanceRecord, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'version' | 'createdBy' | 'updatedBy'> {
    const now = Date.now()
    const attendanceDate = new Date(now).toISOString().slice(0, 10)

    return {
      tenantId,
      employeeId,
      employeeName,
      attendanceDate,
      logTime: now,
      type,
      source: photoUrl ? 'mobile_photo' as AttendanceSource : 'mobile_gps' as AttendanceSource,
      latitude,
      longitude,
      locationName,
      photoUrl,
      gpsAccuracy,
      isLate: false,
      lateMinutes: 0,
      isOvertime: false,
      overtimeMinutes: 0,
      isUndertime: false,
      undertimeMinutes: 0,
      status: 'pending',
      notes: `Mobile ${type} at ${locationName ?? 'unknown location'}`,
    }
  }

  /**
   * Disconnect all active drivers.
   */
  async disconnectAll(): Promise<void> {
    for (const [id, driver] of this.activeDrivers.entries()) {
      try {
        await driver.disconnect()
      } catch (e) {
        console.error(`[Biometric] Error disconnecting driver ${id}:`, e)
      }
    }
    this.activeDrivers.clear()
  }
}
